import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Save, Edit3 } from 'lucide-react';
import type { Project } from './ProjectSidebar';

interface ProjectContextProps {
  project: Project;
  onProjectUpdate: (projectId: string, updates: Partial<Project>) => void;
}

export function ProjectContext({ project, onProjectUpdate }: ProjectContextProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(project.name || '');
  const [editedDescription, setEditedDescription] = useState(project.description || '');
  const [editedContext, setEditedContext] = useState(project.context || '');

  useEffect(() => {
    setEditedName(project.name || '');
    setEditedDescription(project.description || '');
    setEditedContext(project.context || '');
  }, [project]);

  const handleSave = () => {
    onProjectUpdate(project.id, {
      name: editedName,
      description: editedDescription,
      context: editedContext,
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedName(project.name || '');
    setEditedDescription(project.description || '');
    setEditedContext(project.context || '');
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle>Project Information</CardTitle>
            <CardDescription>
              Basic information about your project
            </CardDescription>
          </div>
          <Button
            onClick={() => setIsEditing(!isEditing)}
            variant="outline"
            size="sm"
          >
            <Edit3 className="h-4 w-4 mr-2" />
            {isEditing ? 'Cancel' : 'Edit'}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="project-name">Project Name</Label>
            {isEditing ? (
              <Input
                id="project-name"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                placeholder="Enter project name"
              />
            ) : (
              <p className="p-3 bg-muted rounded-md">{project.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="project-description">Description</Label>
            {isEditing ? (
              <Textarea
                id="project-description"
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                placeholder="Enter project description"
                rows={3}
              />
            ) : (
              <p className="p-3 bg-muted rounded-md min-h-[80px]">
                {project.description || 'No description provided'}
              </p>
            )}
          </div>

          {isEditing && (
            <div className="flex gap-2 pt-2">
              <Button onClick={handleSave} size="sm">
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
              <Button onClick={handleCancel} variant="outline" size="sm">
                Cancel
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Project Context</CardTitle>
          <CardDescription>
            Provide context that will be shared with the AI in all chat sessions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Textarea
              value={editedContext}
              onChange={(e) => setEditedContext(e.target.value)}
              placeholder="Enter project context, requirements, constraints, or any information that should be available to the AI across all chat sessions..."
              rows={12}
              className="min-h-[300px]"
            />
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                {(editedContext || '').length} characters
              </p>
              <Button 
                onClick={() => onProjectUpdate(project.id, { context: editedContext })}
                size="sm"
                disabled={editedContext === project.context}
              >
                <Save className="h-4 w-4 mr-2" />
                Save Context
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}